import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getTokenFromStorage, showError } from '../utility/common-logic';
import { ApiService } from './api-services/api.service';
import { SharedService } from './shared.service';

interface ChatClientPostMessage {
  action?: string;
  source?: string;
}

interface ChatClientSettings {
  isShow?: boolean;
  devUrl?: string;
  prodDomains?: Record<string, string>;
  anonymousPath?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatClientService {
  panelOpenSubject = new BehaviorSubject(false);
  iframeUrlSubject = new BehaviorSubject<string | null>(null);
  loadingSubject = new BehaviorSubject(false);
  openingPanel: boolean = false;
  launchingChatUi: boolean = false;

  panelOpen$ = this.panelOpenSubject.asObservable();
  iframeUrl$ = this.iframeUrlSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  handleChatClientMessage = (event: MessageEvent) => {
    if (!this.isEnabled || !this.isPanelOpen) {
      return;
    }

    if (!this.allowedOrigins.has(event.origin)) {
      return;
    }

    const data = this.parsePostMessage(event.data);
    if (data?.source !== 'aira' || data?.action !== 'close_chat') {
      return;
    }

    this.ngZone.run(() => this.close());
  };

  constructor(
    private api: ApiService,
    private shared: SharedService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private ngZone: NgZone,
  ) {
    window.addEventListener('message', this.handleChatClientMessage);
  }

  private get chatClient(): ChatClientSettings | undefined {
    return environment.settings.chatClient as ChatClientSettings | undefined;
  }

  private get allowedOrigins(): Set<string> {
    return this.buildAllowedOrigins();
  }

  get isEnabled(): boolean {
    return !!this.chatClient?.isShow;
  }

  get isPanelOpen(): boolean {
    return this.panelOpenSubject.value;
  }

  wireFreshChatLauncher(fcWidget: any, ngZone: NgZone): void {
    if (!fcWidget) {
      return;
    }

    if (!this.isEnabled) {
      document.body.classList.remove('chat-ui-enabled');
      if (fcWidget.__chatUiOriginalOpen) {
        fcWidget.open = fcWidget.__chatUiOriginalOpen;
      }
      return;
    }

    document.body.classList.add('chat-ui-enabled');

    if (!fcWidget.__chatUiOriginalOpen) {
      fcWidget.__chatUiOriginalOpen = fcWidget.open?.bind(fcWidget);
    }

    const openChatUi = () => {
      if (this.launchingChatUi || this.isPanelOpen) {
        return;
      }
      this.launchingChatUi = true;
      this.setLauncherHidden(true);
      fcWidget.close();
      ngZone.run(() => this.open());
    };

    fcWidget.open = openChatUi;

    if (!fcWidget.__chatUiOpenedHook) {
      fcWidget.on('widget:opened', () => {
        if (this.isEnabled) {
          openChatUi();
        }
      });
      fcWidget.__chatUiOpenedHook = true;
    }

    this.setLauncherHidden(this.isPanelOpen);
  }

  open(): void {
    if (!this.isEnabled) {
      return;
    }

    if (this.isPanelOpen) {
      this.close();
      return;
    }

    if (!this.baseUrl) {
      showError(
        this.translate.instant('msg.error._'),
        this.toastr,
        this.translate,
        true,
      );
      return;
    }

    const hasToken =
      !!getTokenFromStorage() || !!this.shared.auth$?.auth?.token;

    try {
      this.markOpeningPanel();

      if (!hasToken) {
        this.showPanel(this.getAnonymousUrl());
        return;
      }

      this.loadingSubject.next(true);

      this.api
        .getDeepLinkToken()
        .then((res) => {
          if (res?.deeplink_token) {
            this.showPanel(
              `${this.baseUrl}/?deeplink_token=${encodeURIComponent(res.deeplink_token)}`,
            );
          } else {
            this.close();
            showError(
              this.translate.instant('msg.error._'),
              this.toastr,
              this.translate,
              true,
            );
          }
        })
        .catch((err) => {
          this.close();
          showError(err, this.toastr, this.translate);
        })
        .finally(() => this.loadingSubject.next(false));
    } finally {
      this.launchingChatUi = false;
    }
  }

  close(): void {
    this.panelOpenSubject.next(false);
    this.iframeUrlSubject.next(null);
    this.loadingSubject.next(false);
    this.launchingChatUi = false;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!this.isPanelOpen) {
          this.setLauncherHidden(false);
        }
      });
    });
  }

  get baseUrl(): string {
    if (!environment.production) {
      return (this.chatClient?.devUrl || '').replace(/\/$/, '');
    }

    const prodDomains = this.chatClient?.prodDomains;
    if (!prodDomains) {
      return '';
    }

    const host = window.location.hostname || '';
    const tld = host.split('.').pop() || 'com';
    const mapped = (prodDomains[tld] || prodDomains['com'] || '').trim();
    return mapped ? mapped.replace(/\/$/, '') : '';
  }

  showPanel(url: string): void {
    if (!url?.trim()) {
      this.close();
      return;
    }

    this.iframeUrlSubject.next(url);
    this.panelOpenSubject.next(true);
    this.loadingSubject.next(false);
    this.setLauncherHidden(true);
  }

  markOpeningPanel(): void {
    this.setLauncherHidden(true);
    this.openingPanel = true;
    this.panelOpenSubject.next(true);
    window.setTimeout(() => {
      this.openingPanel = false;
    }, 0);
  }

  setLauncherHidden(hidden: boolean): void {
    if (!this.isEnabled) {
      return;
    }

    const fcWidget = (window as any).fcWidget;

    if (hidden) {
      document.body.classList.add('chat-ui-panel-open');
      if (fcWidget) {
        fcWidget.close();
        fcWidget.hide();
      }
      return;
    }

    const freshchat = environment.settings.freshchat;
    const showLauncher =
      !!freshchat?.isShow &&
      (freshchat.pages === 'all' || freshchat.pages === 'support');

    if (fcWidget && showLauncher) {
      fcWidget.show();
      fcWidget.close();
    }

    window.setTimeout(() => {
      document.body.classList.remove('chat-ui-panel-open');
    }, 150);
  }

  getAnonymousUrl(): string {
    const path = this.chatClient?.anonymousPath || '/anonymous';
    const anonymousPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${anonymousPath}`;
  }

  buildAllowedOrigins(): Set<string> {
    const origins = new Set<string>();

    this.addOriginFromUrl(this.chatClient?.devUrl, origins);

    const prodDomains = this.chatClient?.prodDomains;
    if (prodDomains) {
      Object.values(prodDomains).forEach((domainUrl) =>
        this.addOriginFromUrl(domainUrl, origins),
      );
    }

    this.addOriginFromUrl(this.baseUrl, origins);

    return origins;
  }

  addOriginFromUrl(url: string | undefined, origins: Set<string>): void {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return;
    }

    const normalized = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;

    try {
      const parsed = new URL(normalized);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        origins.add(parsed.origin);
      }
    } catch {
      return;
    }
  }

  parsePostMessage(data: unknown): ChatClientPostMessage | null {
    if (!data) {
      return null;
    }

    if (typeof data === 'string') {
      try {
        return JSON.parse(data) as ChatClientPostMessage;
      } catch {
        return null;
      }
    }

    if (typeof data === 'object') {
      return data as ChatClientPostMessage;
    }

    return null;
  }
}
